import { ActionIconProps, Mark, Text, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, forwardRef } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { deleteTiposDeRespostas } from "../../firestore";
import { QUERY_KEY } from "../tipos-de-respostas-table/TiposDeRespostasTable";

type RemoveButtonProperties = PropsWithChildren<
  ActionIconProps & {
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    tiposDeRespostas: TTiposDeRespostas;
  }
>;
type TDeleteResponse = Awaited<ReturnType<typeof deleteTiposDeRespostas>>;

const RemoveTiposDeRespostasButton = forwardRef<HTMLButtonElement, RemoveButtonProperties>(
  ({ tiposDeRespostas, children, ...properties }, reference) => {
    const queryClient = useQueryClient();
    const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

    const { mutate: deleteTiposDeRespostasMutation, isPending } = useMutation<TDeleteResponse, Error>({
      mutationFn: () => {
        if (!activeEmpresaId) {
          throw new Error("Empresa não selecionada");
        }
        return deleteTiposDeRespostas(activeEmpresaId, tiposDeRespostas);
      },
      onSuccess() {
        return queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
      onError(error) {
        captureException(error, true);
      }
    });

    /**
     * Handles the click event on the button.
     * @param event - The event that triggered the click.
     */
    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      modals.openConfirmModal({
        title: "Remover tipo de resposta",
        centered: true,
        children: (
          <Text size="sm">
            Tem certeza que deseja remover o tipo de resposta <Mark>{tiposDeRespostas.nome}</Mark>
          </Text>
        ),
        labels: {
          confirm: "Sim",
          cancel: "Não"
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          void deleteTiposDeRespostasMutation();
        }
      });
    }

    return (
      <UnstyledButton disabled={isPending} ref={reference} {...properties} onClick={handleClick}>
        {children}
      </UnstyledButton>
    );
  }
);

RemoveTiposDeRespostasButton.displayName = "RemoveTiposDeRespostasButton";

export default RemoveTiposDeRespostasButton;
